import React from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { Input, Select, SelectItem, Button, Card, CardBody, Chip, Textarea } from "@heroui/react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";

export const DosageForm: React.FC = () => {
	const {
		control,
		formState: { errors },
		watch,
	} = useFormContext<TRequest>();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "dosages",
	});
	const existsDosage = useFieldArray({ control, name: "existsDosage" });

	const dosageTypes = [
		{ value: "flowering", label: "תפרחת קנאביס" },
		{ value: "oil", label: "שמן קנאביס" },
	];

	const centralismTypes = [
		"T22 / C4",
		"T18 / C3",
		"T12 / C2",
		"T1 / C1",
		"T8 / C8",
		"T12 / C12",
		"T3 / C12",
		"T3 / C18",
		"T1 / C22",
		"T1 / C24",
		"T0 / C26",
		"T5 / C5",
		"T15 / C3",
		"T10 / C10",
		"T10 / C2",
		"T5 / C10",
		"T3 / C15",
	];

	const requestType = watch("requestType");
	const hasExistingDosage = requestType === "renew" || requestType === "increase";
	console.log("hasExistingDosage", hasExistingDosage);

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-3 mb-4">
				<div className="bg-success-100 p-2 rounded-full">
					<Icon icon="lucide:leaf" className="h-6 w-6 text-success" />
				</div>
				<div>
					<h2 className="text-xl font-semibold">מינון מבוקש</h2>
					<p className="text-default-500 text-sm">הגדר את סוג וכמות המינון המבוקש</p>
				</div>
			</div>

			{hasExistingDosage && (
				<div className="mb-6 text-start">
					<div className="flex justify-between items-center mb-3">
						<h3 className="text-lg font-medium mb-3">מינון נוכחי</h3>
						<Button
							size="sm"
							color="primary"
							variant="flat"
							startContent={<Icon icon="lucide:plus" className="h-4 w-4" />}
							onPress={() => {
								if (fields.length < 3) {
									existsDosage.append({
										type: "flowering",
										amount: 20,
										centralism: "T10 / C2",
									});
								}
							}}
							isDisabled={fields.length >= 3}
						>
							הוסף מינון
						</Button>
					</div>
					<div className="space-y-4">
						{existsDosage.fields.map((field, index) => {
							return (
								<Card key={field.id} className="bg-default-50">
									<CardBody>
										<div className="flex justify-between items-center mb-3">
											<Chip color="primary" variant="flat">
												מינון {index + 1}
											</Chip>
											<Button
												aria-label="הסר מינון"
												isIconOnly
												size="sm"
												color="danger"
												variant="light"
												onPress={() => existsDosage.remove(index)}
											>
												<Icon icon="lucide:trash-2" className="h-4 w-4" />
											</Button>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<Controller
												name={`existsDosage.${index}.type`}
												control={control}
												render={({ field }) => (
													<Select
														label="סוג קנאביס"
														placeholder="בחר סוג"
														selectedKeys={field.value ? [field.value] : []}
														onChange={(e) => field.onChange(e.target.value)}
														isRequired
														isInvalid={!!errors.existsDosage?.[index]?.type}
														errorMessage={
															(errors.existsDosage?.[index]?.type as any)?.message
														}
													>
														{dosageTypes.map((type) => (
															<SelectItem key={type.value}>{type.label}</SelectItem>
														))}
													</Select>
												)}
											/>

											<Controller
												name={`existsDosage.${index}.amount`}
												control={control}
												render={({ field }) => (
													<Input
														type="number"
														label="כמות בגרמים"
														placeholder="הכנס כמות"
														min={10}
														max={60}
														isRequired
														isInvalid={!!errors.existsDosage?.[index]?.amount}
														errorMessage={errors.existsDosage?.[
															index
														]?.amount?.message?.toString()}
														{...field}
														value={field.value?.toString() || ""}
														endContent={
															<div className="pointer-events-none flex items-center">
																<span className="text-default-400 text-small">
																	גרם
																</span>
															</div>
														}
													/>
												)}
											/>

											<Controller
												name={`existsDosage.${index}.centralism`}
												control={control}
												render={({ field }) => (
													<Select
														label="ריכוז"
														placeholder="בחר ריכוז"
														selectedKeys={field.value ? [field.value] : []}
														onChange={(e) => field.onChange(e.target.value)}
														isRequired
														isInvalid={!!errors.existsDosage?.[index]?.centralism}
														errorMessage={errors.existsDosage?.[
															index
														]?.centralism?.message?.toString()}
													>
														{centralismTypes.map((type) => (
															<SelectItem key={type}>{type}</SelectItem>
														))}
													</Select>
												)}
											/>
										</div>
									</CardBody>
								</Card>
							);
						})}
					</div>
				</div>
			)}

			<div>
				<div className="flex justify-between items-center mb-3">
					<h3 className="text-lg font-medium">מינון מבוקש</h3>
					<Button
						size="sm"
						color="primary"
						variant="flat"
						startContent={<Icon icon="lucide:plus" className="h-4 w-4" />}
						onPress={() => {
							if (fields.length < 3) {
								append({
									type: "flowering",
									amount: 20,
									centralism: "T10 / C10",
								});
							}
						}}
						isDisabled={fields.length >= 3}
					>
						הוסף מינון
					</Button>
				</div>

				<div className="space-y-4">
					{fields.map((field, index) => (
						<Card key={field.id} className="relative">
							<CardBody>
								<div className="flex justify-between items-center mb-3">
									<Chip color="primary" variant="flat">
										מינון {index + 1}
									</Chip>
									{fields.length > 0 && (
										<Button
											aria-label="הסר מינון"
											isIconOnly
											size="sm"
											color="danger"
											variant="light"
											onPress={() => remove(index)}
										>
											<Icon icon="lucide:trash-2" className="h-4 w-4" />
										</Button>
									)}
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<Controller
										name={`dosages.${index}.type`}
										control={control}
										render={({ field }) => (
											<Select
												label="סוג קנאביס"
												placeholder="בחר סוג"
												selectedKeys={field.value ? [field.value] : []}
												onChange={(e) => field.onChange(e.target.value)}
												isRequired
												isInvalid={!!errors.dosages?.[index]?.type}
												errorMessage={
													(errors.dosages?.[index]?.type as any)?.message as any
													// todo
												}
											>
												{dosageTypes.map((type) => (
													<SelectItem key={type.value}>{type.label}</SelectItem>
												))}
											</Select>
										)}
									/>

									<Controller
										name={`dosages.${index}.amount`}
										control={control}
										render={({ field }) => (
											<Input
												type="number"
												label="כמות בגרמים"
												placeholder="הכנס כמות"
												min={10}
												max={60}
												isRequired
												isInvalid={!!errors.dosages?.[index]?.amount}
												errorMessage={errors.dosages?.[
													index
												]?.amount?.message?.toString()}
												{...field}
												value={field.value?.toString() || ""}
												endContent={
													<div className="pointer-events-none flex items-center">
														<span className="text-default-400 text-small">גרם</span>
													</div>
												}
											/>
										)}
									/>

									<Controller
										name={`dosages.${index}.centralism`}
										control={control}
										render={({ field }) => (
											<Select
												label="ריכוז"
												placeholder="בחר ריכוז"
												selectedKeys={field.value ? [field.value] : []}
												onChange={(e) => field.onChange(e.target.value)}
												isInvalid={!!errors.dosages?.[index]?.centralism}
												errorMessage={errors.dosages?.[index]?.centralism?.message}
											>
												{centralismTypes.map((type) => (
													<SelectItem key={type}>{type}</SelectItem>
												))}
											</Select>
										)}
									/>
								</div>
							</CardBody>
						</Card>
					))}
				</div>

				{fields.length < 3 && (
					<div className="mt-4 text-center text-default-500 text-sm">
						ניתן להוסיף עד 3 סוגי מינון
					</div>
				)}
			</div>

			<div className="mt-8">
				<Controller
					name="userComment"
					control={control}
					render={({ field }) => (
						<Textarea
							{...field}
							label="הערות המטופל"
							placeholder="הכנס הערות נוספות (אופציונלי)"
							isInvalid={!!errors.userComment}
							errorMessage={errors.userComment?.message?.toString()}
							maxLength={255}
							description="עד 255 תווים"
						/>
					)}
				/>
			</div>
		</div>
	);
};
